import * as DialogPrimitive from "@radix-ui/react-dialog";
import {
  AnimatePresence,
  animate,
  cubicBezier,
  motion,
  useMotionValue,
} from "motion/react";
import { useMemo, type ReactNode } from "react";

import { cn } from "@/lib/utils";

/** iOS-style top inset; matches React Aria sheet example spacing. */
const GESTURE_BOTTOM_SHEET_TOP_MARGIN = 28;

const gestureBottomSheetStaticTransition = {
  duration: 0.42,
  ease: cubicBezier(0.32, 0.72, 0, 1),
};

const gestureBottomSheetInertiaTransition = {
  type: "inertia" as const,
  bounceStiffness: 300,
  bounceDamping: 40,
  timeConstant: 300,
};

const gestureBottomSheetPanelClassName =
  "pointer-events-auto absolute inset-x-0 bottom-0 z-50 w-full max-h-[calc(100dvh-1.5rem)] cursor-grab overflow-y-auto rounded-t-[20px] border border-[#d5d5d5] border-b-0 bg-white font-[Montserrat,system-ui,sans-serif] shadow-[0_-12px_48px_rgba(0,0,0,0.12)] will-change-transform active:cursor-grabbing";

function useGestureBottomSheetTravel() {
  return useMemo(
    () =>
      typeof window !== "undefined"
        ? Math.max(0, window.innerHeight - GESTURE_BOTTOM_SHEET_TOP_MARGIN)
        : 640,
    [],
  );
}

/** Gesture-driven bottom sheet built on Motion + Radix Dialog primitives. */
export function GestureBottomSheet({
  sheetTravel,
  onOpenChange,
  children,
  panelClassName,
  overlayClassName,
  "aria-label": ariaLabel,
}: {
  sheetTravel: number;
  onOpenChange: (open: boolean) => void;
  children: ReactNode;
  /** Merged with the default panel surface classes. */
  panelClassName?: string;
  /**
   * Merged with the overlay. The overlay is transparent by default — pass e.g.
   * `bg-black/40 backdrop-blur-md` to dim and blur what's behind the sheet, and
   * it will fade in and out with the panel.
   */
  overlayClassName?: string;
  "aria-label"?: string;
}) {
  const y = useMotionValue(sheetTravel);

  return (
    <DialogPrimitive.Root open onOpenChange={onOpenChange}>
      <DialogPrimitive.Portal forceMount>
        <DialogPrimitive.Overlay asChild>
          <motion.div
            className={cn("fixed inset-0 flex items-end", overlayClassName)}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={gestureBottomSheetStaticTransition}
          />
        </DialogPrimitive.Overlay>
        <DialogPrimitive.Content asChild>
          <motion.div
            aria-label={ariaLabel}
            className={cn(gestureBottomSheetPanelClassName, panelClassName)}
            initial={{ y: sheetTravel }}
            animate={{ y: 0 }}
            exit={{ y: sheetTravel }}
            transition={gestureBottomSheetStaticTransition}
            style={{ y }}
            drag="y"
            dragConstraints={{ top: 0 }}
            dragElastic={{ top: 0, bottom: 0.18 }}
            onDragEnd={(_event, { offset, velocity }) => {
              const dismissOffset =
                typeof window !== "undefined" ? window.innerHeight * 0.2 : 160;
              if (offset.y > dismissOffset || velocity.y > 480) {
                onOpenChange(false);
              } else {
                void animate(y, 0, {
                  ...gestureBottomSheetInertiaTransition,
                  min: 0,
                  max: 0,
                });
              }
            }}
          >
            {children}
          </motion.div>
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}

export function GestureBottomSheetPresence({
  isOpen,
  onOpenChange,
  presenceKey,
  children,
  panelClassName,
  overlayClassName,
  "aria-label": ariaLabel,
}: {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  presenceKey: string;
  children: ReactNode;
  panelClassName?: string;
  overlayClassName?: string;
  "aria-label"?: string;
}) {
  const sheetTravel = useGestureBottomSheetTravel();
  return (
    <AnimatePresence>
      {isOpen ? (
        <GestureBottomSheet
          key={presenceKey}
          sheetTravel={sheetTravel}
          onOpenChange={onOpenChange}
          panelClassName={panelClassName}
          overlayClassName={overlayClassName}
          aria-label={ariaLabel}
        >
          {children}
        </GestureBottomSheet>
      ) : null}
    </AnimatePresence>
  );
}
